import * as React from 'react';
import {
  Controller,
  useFormContext,
  type Control,
  type FieldPath,
  type FieldValues,
} from 'react-hook-form';
import { cn } from '@noor/lib/cn';
import { FormFieldProvider, type FormFieldContextValue } from './form-field-context';

export interface FormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  /** RHF field name (typed against the form's values). */
  name: TName;
  /**
   * RHF control. Optional — falls back to the enclosing `FormProvider` via
   * `useFormContext`, which is the common case.
   */
  control?: Control<TFieldValues>;
  className?: string;
  children: React.ReactNode;
}

/**
 * Compound form-field root. Renders an RHF `Controller` and publishes the
 * controlled `field` + a11y ids + error through `FormFieldContext`, so
 * subcomponents (`FormField.Input`, `.Select`, …) auto-wire to the field.
 *
 * RHF owns form state end to end; this component never holds field values.
 * Presentational only — it knows nothing about any domain.
 */
export function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ name, control, className, children }: FormFieldProps<TFieldValues, TName>) {
  const context = useFormContext<TFieldValues>();
  const resolvedControl = control ?? context.control;
  const reactId = React.useId();

  return (
    <Controller
      name={name}
      control={resolvedControl}
      render={({ field, fieldState }) => {
        const value: FormFieldContextValue = {
          name,
          // Field/error are erased to the base FieldValues shape in context;
          // subcomponents read them structurally. The render-prop above stays
          // fully typed against TFieldValues.
          field: field as FormFieldContextValue['field'],
          error: fieldState.error,
          formItemId: `${reactId}-form-item`,
          formDescriptionId: `${reactId}-form-item-description`,
          formMessageId: `${reactId}-form-item-message`,
        };
        return (
          <FormFieldProvider value={value}>
            <div
              data-slot="form-field"
              className={cn('grid gap-[var(--form-field-gap)]', className)}
            >
              {children}
            </div>
          </FormFieldProvider>
        );
      }}
    />
  );
}
